import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { BreadcrumbsComponent } from 'components/breadcrumbs/breadcrumbs.component';
import { ClientActivitiesComponent } from 'components/client-view-sections/client-activities/client-activities.component';
import { LegalEntityNotesFormComponent } from 'components/organization-sections/legal-entity-notes-form/legal-entity-notes-form.component';
import { ContentSideNavComponent } from 'components/content-side-nav/content-side-nav.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { OrganizationGeneralInfoFormComponent } from 'components/organization-sections/organization-general-info-form/organization-general-info-form.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TableComponent } from 'components/table/table.component';
import { API_CLIENT, API_SERVICE_REQUEST, ApiService, DEFAULT_NOTES_PAGE_SIZE } from 'services/api.service';
import { Breadcrumb } from 'types/breadcrumb.interface';
import { ClientCard, ClientStatus } from 'types/client.interface';
import { CountryDto } from 'types/country.interface';
import { LegalEntityCard, LegalEntityFormOption, LegalEntityNoteListItem } from 'types/legal-entity.interface';
import { FilterType, TableColumn } from 'types/table-column.interface';

export const KEY_LAST_ACTIVE_ORG_SECTION = 'lastActiveOrgSection';

@Component({
  selector: 'app-view-organization-page',
  standalone: true,
  imports: [
    BreadcrumbsComponent,
    TranslateModule,
    ContentSideNavComponent,
    OrganizationGeneralInfoFormComponent,
    SpinnerComponent,
    ConfirmModalComponent,
    ClientActivitiesComponent,
    LegalEntityNotesFormComponent,
    TableComponent
  ],
  templateUrl: './view-organization-page.component.html',
  styleUrl: './view-organization-page.component.scss'
})
export class ViewOrganizationPageComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [];
  isLoading: boolean = true;
  legalEntityId?: string;
  legalEntity?: LegalEntityCard;
  navItems: string[] = [
    'general',
    'activities',
    'officers',
    'service_history',
    'notes'
  ];
  activeItem: string = 'general';
  countries: CountryDto[] = [];
  initialActiveItem?: string = 'general';
  hasFormChanged: boolean = false;
  confirmModalOpen: boolean = false;
  nextSection: string = '';
  changeBack: boolean = false;
  mappedClient?: ClientCard;
  officersDefaultFilters: { [key: string]: string } = {};
  disabledNavItems: string[] = [];
  legalEntityNotes?: LegalEntityNoteListItem[];
  notesCount: number = 0;
  notesPage: number = 1;
  notesPageSize: number = DEFAULT_NOTES_PAGE_SIZE;
  isSubmittingNoteFiles: boolean = false;
  legalEntityFormOptions: LegalEntityFormOption[] = [];

  readonly clientApplicationsEndpoint = API_SERVICE_REQUEST;

  getServiceRequestViewPath = (row: any): string => {
    if (row.serviceRequestType === 'InformationRequest') {
      if (row.status === 'Melnraksts') {
        return `/pakalpojumi/informacijas-pieprasijums/${row.id}`;
      }
      return `/informacijas-pieprasijums/${row.id}`;
    }
    return `/pieteikums/${row.id}`;
  };
  readonly officersEndpoint = API_CLIENT;

  get clientId(): string | null {
    return this.legalEntityId ?? null;
  }

  appTableColumns: TableColumn[] = [
    {
      name: 'pages.client.serviceHistory.code',
      key: 'code',
      sortEnabled: true,
      sortKey: 'Code',
      filter: FilterType.Search
    },
    {
      name: 'pages.client.serviceHistory.serviceName',
      key: 'serviceName',
      sortEnabled: true,
      sortKey: 'ServiceName',
      multiselectFilterName: 'serviceRequestType',
      filter: FilterType.MultiSelect,
      filterOptions: ['InformationRequest']
    },
    {
      name: 'pages.client.serviceHistory.submittedOn',
      key: 'submittedOn',
      type: 'date',
      sortEnabled: true,
      sortKey: 'SubmittedOn',
      filter: FilterType.DateTime
    },
    {
      name: 'pages.client.serviceHistory.submitter',
      key: 'submitterFullName',
      sortEnabled: true,
      sortKey: 'SubmitterFullName',
      filter: FilterType.Search
    },
    {
      name: 'pages.client.serviceHistory.status',
      key: 'status',
      styleClass: 'status-column',
      sortEnabled: true,
      sortKey: 'Status',
      multiselectFilterName: 'statuses',
      filter: FilterType.MultiSelect,
      dynamicFilterOptionsField: 'status',
      dynamicFilterSourceField: 'statusCounts',
      dynamicFilterSourceIdKey: 'status',
      dynamicFilterSourceLabelKey: 'status'
    },
    {
      name: 'pages.client.serviceHistory.result',
      key: 'result',
      sortEnabled: true,
      sortKey: 'Result',
      filter: FilterType.Search
    }
  ];
  officersColumns: TableColumn[] = [
    {
      name: 'pages.client.tableColumns.officerName',
      key: 'fullName',
      sortEnabled: true,
      sortKey: 'fullName'
    },
    {
      name: 'pages.client.tableColumns.phone',
      key: 'phone',
      sortKey: 'phone',
      sortEnabled: true
    },
    {
      name: 'pages.client.tableColumns.email',
      key: 'email',
      sortKey: 'email',
      sortEnabled: true
    },
    {
      name: 'pages.client.tableColumns.status',
      key: 'status',
      sortEnabled: true,
      sortKey: 'status',
      styleClass: 'status-column',
      valueTranslate: 'pages.client.FrontofficeReadingRoomSubmissionStatus.'
    }
  ];

  constructor(
    private apiService: ApiService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params.subscribe((params) => {
      this.legalEntityId = params['id'];

      if (this.legalEntityId) {
        this.officersDefaultFilters = { legalEntityId: this.legalEntityId };
        this.loadData();
        this.initialActiveItem = localStorage.getItem(KEY_LAST_ACTIVE_ORG_SECTION) || 'general';
        this.activeItem = this.initialActiveItem;
      }
    });
  }

  loadData(): void {
    forkJoin({
      legalEntity: this.apiService.getLegalEntity(this.legalEntityId!),
      countries: this.apiService.getCountries(),
      legalEntityForms: this.apiService.getLegalEntityForms()
    }).subscribe({
      next: ({ legalEntity, countries, legalEntityForms }) => {
        this.legalEntity = legalEntity;
        this.legalEntityFormOptions = legalEntityForms;
        this.mappedClient = this.mapLegalEntityToClient(legalEntity);
        this.countries =
          countries.list?.sort((a, b) => {
            if (a.code === 'LV') return -1;
            if (b.code === 'LV') return 1;
            return (a.name ?? '').localeCompare(b.name ?? '', 'lv');
          }) ?? [];
        this.updateDisabledNavItems(legalEntity);
        this.isLoading = false;
        this.setBreadcrumbs();
        this.loadNotes();
      },
      error: (error) => {
        console.error(error);
      }
    });
  }

  loadNotes(): void {
    if (this.legalEntityId) {
      this.apiService.getAllLegalEntityNotes(this.legalEntityId, this.notesPage, this.notesPageSize).subscribe({
        next: (data: LegalEntityNoteListItem[]) => {
          this.legalEntityNotes = data;
          this.notesCount = data?.length ?? 0;
        },
        error: () => {
          this.legalEntityNotes = [];
          this.notesCount = 0;
        }
      });
    }
  }

  onNotesPageChange(page: number): void {
    this.notesPage = page;
    this.loadNotes();
  }

  private updateDisabledNavItems(legalEntity: LegalEntityCard): void {
    const isManuallyCreated = legalEntity.authenticationType !== 'NationalId';
    const noOfficerAuthenticated = !legalEntity.loggedOn;

    if (isManuallyCreated && noOfficerAuthenticated) {
      this.disabledNavItems = ['officers'];
    } else {
      this.disabledNavItems = [];
    }
  }

  getOrganizationHeading(): string {
    if (!this.legalEntity) return '';
    const typeLabel = this.legalEntityFormOptions.find(o => o.code === this.legalEntity?.type)?.name || '';
    return typeLabel ? `${this.legalEntity.name}, ${typeLabel}` : this.legalEntity.name;
  }

  setBreadcrumbs(): void {
    const heading = this.getOrganizationHeading();
    this.breadcrumbs = [
      {
        title: 'components.breadcrumbs.organizationList',
        url: '/klienti/juridiskas-personas'
      },
      {
        title: `${heading} (${this.legalEntity?.registrationNumber})`,
        url: `/juridiska-persona/${this.legalEntity?.id}`
      }
    ];
  }

  onSectionChanged(activeItem: string, confirmed: boolean): void {
    if (activeItem === this.activeItem && confirmed) return;
    this.nextSection = activeItem;

    if (!confirmed) {
      this.initialActiveItem = this.activeItem;
      this.changeBack = !this.changeBack;
      this.confirmModalOpen = true;
    } else {
      this.activeItem = this.nextSection;
      this.initialActiveItem = this.activeItem;
      this.changeBack = !this.changeBack;
      this.hasFormChanged = false;
      localStorage.setItem(KEY_LAST_ACTIVE_ORG_SECTION, this.activeItem);
    }
  }

  mapLegalEntityToClient(entity: LegalEntityCard): ClientCard {
    const isPortalRegistered = entity.authenticationType === 'NationalId';
    const creatorName = !isPortalRegistered && entity.creator
      ? `${entity.creator.firstName} ${entity.creator.lastName}`
      : undefined;

    return {
      id: entity.id,
      createdOn: entity.createdOn,
      authenticationType: entity.authenticationType,
      isDeleted: entity.isDeleted,
      creatorName,
      registrationDate: isPortalRegistered ? entity.createdOn : undefined,
      loggedOn: entity.loggedOn,
      modifiedOn: entity.modifiedOn
    } as ClientCard;
  }

  onLegalEntityUpdated(updatedLegalEntity: LegalEntityCard): void {
    this.legalEntity = updatedLegalEntity;
    this.mappedClient = this.mapLegalEntityToClient(updatedLegalEntity);
    this.updateDisabledNavItems(updatedLegalEntity);
    this.setBreadcrumbs();
  }

  navigateToInfoRequest(): void {
    this.router.navigate([`/${this.legalEntityId}/pakalpojumi/informacijas-pieprasijums`], {
      queryParams: { clientType: 2 }
    });
  }
}
